import { Icon, Layout, ProductImg, Text } from 'ds';
import { ConsumablesOrderItem } from 'shared';

interface ConsumablesOrderProductPreviewProps {
  consumablesOrderItems: ConsumablesOrderItem[];
  useHorizontalMobileLayout?: boolean;
  size?: 'small' | 'default';
  maxVisibleItems?: number;
}

const ConsumablesOrderProductPreview = ({
  consumablesOrderItems,
  useHorizontalMobileLayout = false,
  size = 'default',
  maxVisibleItems = 3
}: ConsumablesOrderProductPreviewProps) => {
  const productImages = consumablesOrderItems.map(item => item.consumables_product?.image_key).filter(Boolean);
  let orderImageContent;

  // Size configurations
  const isSmall = size === 'small';
  const imageSize = isSmall ? 22 : 32;
  const gridSize = isSmall ? 48 : 68; // (imageSize * 2) + 4
  const horizontalImageSize = isSmall ? 22 : 64;
  const thumbnailSize = isSmall ? 'smallThumbnail' : 'thumbnail';
  const iconSize = isSmall ? 'lg' : 'xxl';
  const badgeSize = isSmall ? 20 : 32;
  const badgeBorderRadius = 4 as const;

  if (productImages.length === 0) {
    // 0 items: Show miscellaneous icon with no count badge
    orderImageContent = (
      <Layout position="relative">
        <Icon name="miscellaneous" size={iconSize} color="gray-500" />
      </Layout>
    );
  } else if (productImages.length === 1) {
    // 1 item: Show single product image with no count badge
    orderImageContent = (
      <Layout position="relative">
        <ProductImg srcKey={productImages[0]} alt="product" size={thumbnailSize} />
      </Layout>
    );
  } else if (productImages.length < 4) {
    // 2-3 items: Show single product image with +N badge
    if (useHorizontalMobileLayout) {
      const itemsToShow = Math.min(productImages.length, maxVisibleItems);
      const hasMoreItems = productImages.length > maxVisibleItems;
      orderImageContent = (
        <Layout flex direction="row" justify="space-between" width="100%">
          {productImages.slice(0, itemsToShow).map((imageKey, index) => (
            <ProductImg
              key={index}
              srcKey={imageKey}
              alt="product"
              size="thumbnail"
              style={{ width: horizontalImageSize, height: horizontalImageSize }}
            />
          ))}
          {hasMoreItems && (
            <Layout
              color="gray-25"
              borderRadius={8}
              width={horizontalImageSize}
              height={horizontalImageSize}
              align="center"
              justify="center"
            >
              <Text size="body-md" color="gray-700" semibold>
                +{consumablesOrderItems.length - maxVisibleItems}
              </Text>
            </Layout>
          )}
        </Layout>
      );
    } else {
      orderImageContent = (
        <Layout position="relative">
          <ProductImg srcKey={productImages[0]} alt="product" size={thumbnailSize} />
          <Layout
            position="absolute"
            color="gray-25"
            borderRadius={badgeBorderRadius}
            width={badgeSize}
            height={badgeSize}
            align="center"
            justify="center"
            bottom={0}
            right={0}
          >
            <Text color="gray-700" size="tag">
              +{consumablesOrderItems.length - 1}
            </Text>
          </Layout>
        </Layout>
      );
    }
  } else {
    // 4+ items: Show 2x2 grid with 3 images and +N badge
    if (useHorizontalMobileLayout) {
      const itemsToShow = maxVisibleItems;
      orderImageContent = (
        <Layout flex direction="row" justify="space-between" width="100%">
          {productImages.slice(0, itemsToShow).map((imageKey, index) => (
            <ProductImg
              key={index}
              srcKey={imageKey}
              alt="product"
              size="thumbnail"
              style={{ width: horizontalImageSize, height: horizontalImageSize }}
            />
          ))}
          <Layout
            color="gray-25"
            borderRadius={8}
            width={horizontalImageSize}
            height={horizontalImageSize}
            align="center"
            justify="center"
          >
            <Text size="body-md" color="gray-700" semibold>
              +{consumablesOrderItems.length - maxVisibleItems}
            </Text>
          </Layout>
        </Layout>
      );
    } else {
      orderImageContent = (
        <Layout flex align="center" justify="center">
          <Layout flex gap={4} direction="column" width={gridSize} height={gridSize} minWidth={gridSize}>
            <Layout flex gap={4}>
              <ProductImg
                srcKey={productImages[0]}
                alt="product"
                size="thumbnail"
                style={{ width: imageSize, height: imageSize }}
              />
              <ProductImg
                srcKey={productImages[1]}
                alt="product"
                size="thumbnail"
                style={{ width: imageSize, height: imageSize }}
              />
            </Layout>
            <Layout flex gap={4}>
              <ProductImg
                srcKey={productImages[2]}
                alt="product"
                size="thumbnail"
                style={{ width: imageSize, height: imageSize }}
              />
              <Layout
                color="gray-25"
                borderRadius={badgeBorderRadius}
                width={imageSize}
                height={imageSize}
                align="center"
                justify="center"
              >
                <Text color="gray-700" size="tag">
                  +{consumablesOrderItems.length - 3}
                </Text>
              </Layout>
            </Layout>
          </Layout>
        </Layout>
      );
    }
  }
  return orderImageContent;
};

export default ConsumablesOrderProductPreview;
